import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, CheckCircle2, Facebook, Instagram, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * In-page setup guidance for the Integrations/Connections tab: what you need
 * before connecting, how to connect Facebook/Messenger, and how to connect
 * Instagram (which goes through the Facebook Page). Collapsible so it stays out
 * of the way for returning users (collapsed by default).
 */
export function ConnectionGuide() {
  const [open, setOpen] = useState(false);

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2 text-left transition-colors hover:bg-slate-100/80 dark:hover:bg-slate-850"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <HelpCircle className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Channel Connection Guide &amp; Requirements
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-850 px-1.5 py-0.2 text-[9px] font-bold text-slate-500 dark:text-slate-400">
            {open ? "Hide Guide" : "View Setup Steps"}
          </span>
        </div>
        <ChevronDown className={cn('h-3.5 w-3.5 text-slate-400 transition-transform duration-300 ease-out', open && 'rotate-180')} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="guide-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
              opacity: { duration: 0.25, ease: 'easeInOut' },
            }}
            className="overflow-hidden"
          >
            <div className="space-y-4 p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70">
              {/* Requirements */}
              <div>
                <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Before you connect
                </h3>
                <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 text-xs text-slate-700 dark:text-slate-300">
                  {[
                    'A Facebook Page you are an admin of (personal profiles do not work).',
                    'For Instagram: an Instagram Business/Professional account linked to that Page.',
                    'Be logged into Facebook as the Page admin in this browser.',
                    'While in dev mode, only registered roles can test incoming messages.',
                  ].map((req) => (
                    <li key={req} className="flex items-start gap-2 rounded-lg border border-slate-200/60 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850/60 p-2">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      <span className="leading-snug">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Connect Facebook / Messenger */}
                <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs">
                  <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wide">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-channel-messenger/10 text-channel-messenger">
                      <Facebook className="h-3 w-3" />
                    </span>
                    Connect Facebook &amp; Messenger
                  </h3>
                  <ol className="ml-1 list-inside list-decimal space-y-1 text-xs text-slate-600 dark:text-slate-300">
                    <li>Click <span className="font-semibold text-slate-800 dark:text-slate-100">Connect</span> on the Facebook card below.</li>
                    <li>Log into Facebook and authorize permissions.</li>
                    <li>Grant access to the Page(s) you wish to automate.</li>
                    <li>Messages will immediately route to your OmnibizChat inbox.</li>
                  </ol>
                </div>

                {/* Connect Instagram */}
                <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs">
                  <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wide">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-channel-instagram/10 text-channel-instagram">
                      <Instagram className="h-3 w-3" />
                    </span>
                    Connect Instagram
                  </h3>
                  <ol className="ml-1 list-inside list-decimal space-y-1 text-xs text-slate-600 dark:text-slate-300">
                    <li>On FB: <span className="font-semibold text-slate-800 dark:text-slate-100">Page → Linked accounts → Instagram</span>.</li>
                    <li>Log in as your Instagram <span className="font-semibold text-slate-800 dark:text-slate-100">Business</span> account.</li>
                    <li>Turn <span className="font-semibold text-slate-800 dark:text-slate-100">"Allow access to messages in Inbox"</span> ON.</li>
                    <li>Click <span className="font-semibold text-slate-800 dark:text-slate-100">Connect</span> on the Instagram card below.</li>
                  </ol>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-xl border border-amber-200/80 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/40 p-2.5 text-xs text-amber-800 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="leading-snug">
                  The Instagram account must be a Business/Professional account linked to the Facebook Page, with "Allow access to messages" enabled — otherwise direct messages cannot be synced.
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default ConnectionGuide;
